import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

const FUNCTION_BASE = 'https://opnawbebzrjsaeoeqgkd.supabase.co/functions/v1/product-images';

// Cache for image URLs to avoid repeated Supabase calls
// Using Map for better performance and includes timestamp for cache invalidation
const imageUrlCache = new Map<string, { url: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
// Storage access cache to avoid repeated failed checks
const storageAccessCache = new Map<string, { success: boolean; timestamp: number; message: string; canList: boolean; canRead: boolean; bucketExists: boolean }>();
const STORAGE_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Robust storage access checker with retry logic and fallback mechanisms
 * @returns Promise resolving to access status and details
 */
export const checkStorageAccess = async (): Promise<boolean> => {
  try {
    console.log("[imageUtils] Checking storage access via write/delete probe...");
    
    // Write/delete probe - most reliable test for upload capability
    const testKey = `_healthchecks/${crypto.randomUUID()}.png`;
    // 1x1 transparent PNG
    const pngBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMB/aklDQAAAABJRU5ErkJggg==';
    const byteString = atob(pngBase64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/png' });
    
    const { error: uploadErr } = await supabase.storage
      .from('product-images')
      .upload(testKey, blob, { upsert: true, contentType: 'image/png' });
    
    if (uploadErr) {
      console.error("[imageUtils] Storage write test failed:", uploadErr);
      return false;
    }
    
    // Cleanup test file (best effort)
    await supabase.storage.from('product-images').remove([testKey]).catch(() => {});
    
    console.log("[imageUtils] Storage access confirmed");
    return true;
  } catch (error) {
    console.error("[imageUtils] Storage access error:", error);
    return false;
  }
};

/**
 * Ensures storage bucket exists - checks if it exists
 * @returns Promise resolving to true if bucket exists
 */
export const ensureStorageBucketExists = async (): Promise<boolean> => {
  try {
    logger.debug("🔍 Checking if product-images bucket exists...");
    
    const isAccessible = await checkStorageAccess();
    return isAccessible;
  } catch (error) {
    logger.warn('Exception during bucket check:', error);
    return false;
  }
};

/**
 * Resolves any image URL to a transformed URL using Edge Function proxy for reliability
 * Always returns optimized URLs for display (2000x2000, contain, webp)
 */
export const getProductImageUrl = (imageUrl: string | null): string => {
  if (!imageUrl || imageUrl === "") {
    return "/placeholder.svg";
  }
  
  // If it's a placeholder, return it directly
  if (imageUrl === "/placeholder.svg") {
    return imageUrl;
  }
  
  // Check cache first
  const cachedEntry = imageUrlCache.get(imageUrl);
  if (cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_DURATION) {
    if (process.env.NODE_ENV === 'development') {
      logger.debug("🟢 Cache HIT for:", imageUrl);
    }
    return cachedEntry.url;
  }
  
  let finalUrl: string;
  
  try {
    // Handle different URL formats
    if (imageUrl.startsWith('http')) {
      // Convert Supabase storage URLs to proxy format
      if (imageUrl.includes('/storage/v1/object/public/product-images/')) {
        const pathMatch = imageUrl.match(/\/product-images\/(.+)$/);
        if (pathMatch) {
          const fileName = pathMatch[1];
          finalUrl = `${FUNCTION_BASE}/${fileName}`;
        } else {
          finalUrl = imageUrl;
        }
      } else {
        // External URLs - keep as-is
        finalUrl = imageUrl;
      }
    }
    // Already a proxy URL
    else if (imageUrl.startsWith('/photo/') || imageUrl.startsWith(`${FUNCTION_BASE}/`)) {
      finalUrl = imageUrl.startsWith('/photo/') 
        ? `${FUNCTION_BASE}/${imageUrl.replace('/photo/','')}` 
        : imageUrl;
    }
    // Handle site assets with leading slash
    else if (imageUrl.startsWith('/')) {
      // For lovable-uploads and other public assets, return as-is
      if (imageUrl.startsWith('/lovable-uploads/')) {
        finalUrl = imageUrl;
      } else {
        finalUrl = imageUrl;
      }
    }
    // Handle lovable-uploads without leading slash
    else if (imageUrl.startsWith('lovable-uploads/')) {
      finalUrl = `/${imageUrl}`;
    }
    // Handle bare filenames (storage keys) - use proxy format
    else {
      // Normalize the path for consistent handling
      let finalPath = imageUrl.replace(/^\/+/, '');
      if (finalPath.startsWith('product-images/')) {
        finalPath = finalPath.replace('product-images/', '');
      }
      
      finalUrl = `${FUNCTION_BASE}/${finalPath}`;
      if (process.env.NODE_ENV === 'development') {
        logger.debug("🔄 Converting storage key to proxy URL:", imageUrl, "->", finalUrl);
      }
    }
    
    // Cache the result
    imageUrlCache.set(imageUrl, { url: finalUrl, timestamp: Date.now() });
    
    return finalUrl;
  } catch (error) {
    console.warn('Error generating product image URL:', error, 'for imageUrl:', imageUrl);
    return '/placeholder.svg';
  }
};

/**
 * Returns base Supabase storage URL without transformation params
 * Transformation params can cause 404s in some edge cases, so we use direct URLs
 */
const addTransformationParams = (baseUrl: string): string => {
  // Return direct Storage URL without any transformation params
  // This ensures maximum compatibility and reliability
  return baseUrl;
};

// Clear image URL from cache - useful after uploads
export const clearImageUrlCache = (imageUrl?: string): void => {
  if (imageUrl) {
    // Create array of all possible cache keys for this image
    const keysToDelete = [imageUrl];
    
    // If it's a direct Supabase URL, add related versions
    if (imageUrl.includes('supabase.co/storage/v1/object/public/product-images/')) {
      const pathMatch = imageUrl.match(/\/product-images\/(.+)$/);
      if (pathMatch) {
        const imagePath = pathMatch[1];
        keysToDelete.push(`/photo/${imagePath}`); // old proxy format
        keysToDelete.push(`${FUNCTION_BASE}/${imagePath}`); // absolute proxy URL
        keysToDelete.push(imagePath); // normalized path
      }
    }
    
    // If it's a proxy URL (old relative or new absolute), add variants
    if (imageUrl.startsWith('/photo/') || imageUrl.startsWith(`${FUNCTION_BASE}/`)) {
      const imagePath = imageUrl.startsWith('/photo/') 
        ? imageUrl.replace('/photo/', '')
        : imageUrl.replace(`${FUNCTION_BASE}/`, '');
      keysToDelete.push(imagePath);
      keysToDelete.push(`https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${imagePath}`);
      keysToDelete.push(`/photo/${imagePath}`);
      keysToDelete.push(`${FUNCTION_BASE}/${imagePath}`);
    }
    
    // If it's a normalized path, add direct URL
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/') && imageUrl !== "placeholder.svg") {
      keysToDelete.push(`https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${imageUrl}`);
    }
    
    // Delete all possible cache keys
    keysToDelete.forEach(key => {
      if (imageUrlCache.has(key)) {
        imageUrlCache.delete(key);
        logger.debug("Cleared cache for key:", key);
      }
    });
    
  } else {
    // Clear entire cache if no specific URL is provided
    const cacheSize = imageUrlCache.size;
    imageUrlCache.clear();
    logger.debug(`Cleared entire image URL cache (${cacheSize} entries)`);
  }
};

// Clean up expired cache entries
const cleanupExpiredCache = (): void => {
  const now = Date.now();
  let cleanedCount = 0;
  
  for (const [key, value] of imageUrlCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      imageUrlCache.delete(key);
      cleanedCount++;
    }
  }
  
  if (cleanedCount > 0) {
    logger.debug(`Cleaned up ${cleanedCount} expired cache entries`);
  }
};

// Run cleanup every 10 minutes for better cache management
setInterval(cleanupExpiredCache, 10 * 60 * 1000);

// Only clean up expired entries on module load, don't clear everything
cleanupExpiredCache();

// Clean up expired entries when page becomes visible (don't clear everything)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Only clean up expired entries, not the entire cache
      cleanupExpiredCache();
    }
  });
}

/**
 * Extract product images with consistent format from any product object
 * Handles both legacy image_url and new product_images array
 */
export const getProductImages = (product: any) => {
  if (!product) return [];
  
  try {
    // If product has product_images array, use it
    if (product.product_images && Array.isArray(product.product_images) && product.product_images.length > 0) {
      // Sort by display_order if available
      const sortedImages = [...product.product_images]
        .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        
      return sortedImages.map(img => ({
        ...img,
        image_url: getProductImageUrl(img.image_url) // Ensure URLs are fully formed
      }));
    }
    
    // Otherwise, create a single image entry from the image_url if it exists
    if (product.image_url) {
      return [{ 
        id: 'legacy-image', 
        image_url: getProductImageUrl(product.image_url), 
        is_primary: true,
        display_order: 0
      }];
    }
    
    // Return empty array if no images
    return [];
  } catch (error) {
    logger.warn("Error processing product images:", error, "for product:", product?.id);
    return [];
  }
};

/**
 * Gets the primary image URL for a product with enhanced fallback
 * Handles both legacy image_url and new product_images array
 */
export const getPrimaryImageUrl = (product: any): string => {
  try {
    // Basic validation
    if (!product) return "/placeholder.svg";
    
    // Get all images using the helper function
    const images = getProductImages(product);
    
    if (!images || images.length === 0) return "/placeholder.svg";
    
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const selectedImage = primaryImage || images[0];
    const imageUrl = selectedImage.image_url;
    
    // Additional validation - ensure we don't return undefined or invalid URLs
    if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') {
      logger.warn("Invalid primary image URL for product:", product?.id, "got:", imageUrl);
      return "/placeholder.svg";
    }
    
    return imageUrl;
  } catch (error) {
    logger.warn("Error getting primary image:", error, "for product:", product?.id);
    return "/placeholder.svg";
  }
};

// Simplified version when you have already extracted the images array
export const getPrimaryImageFromArray = (images: Array<{ image_url: string, is_primary: boolean }> | null): string => {
  if (!images || images.length === 0) return "/placeholder.svg";
  
  try {
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const imageUrl = primaryImage ? primaryImage.image_url : images[0].image_url;
    
    // Ensure the URL is fully formed
    return getProductImageUrl(imageUrl);
  } catch (error) {
    logger.error("Error getting primary image from array:", error);
    return "/placeholder.svg";
  }
};

// Preload high-priority images to improve LCP
export const preloadCriticalImages = (imageUrls: string[]) => {
  imageUrls.forEach(url => {
    if (!url || url === "/placeholder.svg") return;
    
    // Process URL through our cache system first
    const processedUrl = getProductImageUrl(url);
    
    const img = new Image();
    img.src = processedUrl;
    (img as any).fetchpriority = "high";
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    
    // Cache the preloaded image
    img.onload = () => {
      if (process.env.NODE_ENV === 'development') {
        logger.debug("✅ Preloaded critical image:", processedUrl);
      }
    };
    
    img.onerror = () => {
      logger.warn("❌ Failed to preload critical image:", processedUrl);
      // Clear failed image from cache
      clearImageUrlCache(url);
    };
  });
};

// Enhanced cache status checker
export const getCacheStatus = () => {
  const now = Date.now();
  const totalEntries = imageUrlCache.size;
  let expiredEntries = 0;
  let validEntries = 0;
  
  for (const [key, value] of imageUrlCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      expiredEntries++;
    } else {
      validEntries++;
    }
  }
  
  return {
    totalEntries,
    validEntries,
    expiredEntries,
    cacheHitRate: validEntries / totalEntries || 0
  };
};

// Helper to generate image srcset for responsive images
export const generateSrcSet = (imageUrl: string): string => {
  if (!imageUrl || imageUrl === "/placeholder.svg") return imageUrl;
  
  // For this implementation, we're assuming Supabase doesn't have
  // automatic image resizing. In a real implementation with a CDN
  // that supports image resizing, you'd generate multiple sizes.
  return imageUrl;
};

/**
 * Health check for storage accessibility with meaningful error messages
 */
export const checkStorageHealth = async (): Promise<{
  success: boolean;
  message: string;
  recommendations: string[];
}> => {
  console.log("[imageUtils] Starting proxy-based storage health check...");
  
  const recommendations: string[] = [];
  
  try {
    // Check proxy access with GET probe
    const proxyUrl = `${FUNCTION_BASE}/__healthcheck__.txt`;
    const response = await fetch(proxyUrl, { 
      method: 'GET',
      cache: 'no-store'
    });
    
    console.log("[imageUtils] Proxy health check response:", response.status);
    
    // Accept 200, 404, or 206 as signs that proxy and bucket are reachable
    const isReachable = response.status === 200 || response.status === 404 || response.status === 206;
    
    if (!isReachable) {
      return {
        success: false,
        message: `Image proxy is not responding correctly (status: ${response.status}). Images may not load properly.`,
        recommendations: [
          "Verify the product-images edge function is deployed",
          "Check Supabase edge function logs for errors",
          "Ensure the product-images bucket exists",
          "Try refreshing the page"
        ]
      };
    }

    // Optional: Try to list files (may fail due to RLS, which is okay)
    try {
      const { data: files, error: listError } = await supabase.storage
        .from('product-images')
        .list('', { limit: 1 });
      
      if (listError) {
        console.warn("[imageUtils] List operation restricted (expected):", listError.message);
        recommendations.push("Note: Direct bucket listing is restricted (images are served via proxy)");
      } else if (files) {
        console.log("[imageUtils] Listed files successfully, count:", files.length);
      }
    } catch (listErr) {
      console.warn("[imageUtils] List check skipped:", listErr);
      recommendations.push("Images are served via secure proxy (direct listing disabled)");
    }

    return {
      success: true,
      message: "Storage proxy is healthy and images are accessible.",
      recommendations: recommendations.length > 0 ? recommendations : ["All systems operational"]
    };

  } catch (error) {
    console.error("[imageUtils] Health check error:", error);
    return {
      success: false,
      message: `Storage health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      recommendations: [
        "Check your internet connection",
        "Verify Supabase project is online",
        "Check edge function deployment status",
        "Contact support if the issue persists"
      ]
    };
  }
};

/**
 * Enhanced debug utility to check storage status with comprehensive diagnostics
 * Can be called from components to diagnose image issues
 */
export const debugStorageStatus = async () => {
  try {
    logger.debug("🔍 Running comprehensive storage diagnostics...");
    
    // Use the health check function
    const healthResult = await checkStorageHealth();
    
    if (!healthResult.success) {
      return {
        success: false,
        message: healthResult.message,
        recommendations: healthResult.recommendations,
        diagnostics: {
          bucketExists: healthResult.success,
          canList: healthResult.success,
          canRead: healthResult.success
        }
      };
    }
    
    // If basic access works, run additional diagnostics
    const diagnostics = {
      bucketExists: healthResult.success,
      canList: healthResult.success,
      canRead: healthResult.success,
      fileCount: 0,
      sampleFiles: [] as string[],
      publicUrlTest: null as boolean | null
    };
    
    // Try to get file listing if we can list
    if (healthResult.success) {
      try {
        const { data: files, error: filesError } = await supabase.storage
          .from('product-images')
          .list('', { limit: 5 });
          
        if (!filesError && files) {
          diagnostics.fileCount = files.length;
          diagnostics.sampleFiles = files.map(f => f.name);
          
          // Test public URL if we have files
          if (files.length > 0) {
            const testFile = files[0];
            const { data } = supabase.storage
              .from('product-images')
              .getPublicUrl(testFile.name);
              
            try {
              const response = await fetch(data?.publicUrl || '', { method: 'HEAD' });
              diagnostics.publicUrlTest = response.status === 200;
              logger.debug(`Public URL test result: ${diagnostics.publicUrlTest}`);
            } catch (fetchError) {
              logger.debug("Public URL test failed:", fetchError);
              diagnostics.publicUrlTest = false;
            }
          }
        }
      } catch (listError) {
        logger.debug("File listing failed during diagnostics:", listError);
      }
    }
    
    // Test direct URL access pattern
    let directAccessWorking = false;
    if (healthResult.success) {
      try {
        const testUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/test.jpg`;
        const response = await fetch(testUrl, { method: 'HEAD' });
        directAccessWorking = response.status === 404 || response.status === 200;
      } catch (directError) {
        logger.debug("Direct access test failed:", directError);
      }
    }
    
    let message = healthResult.message;
    if (diagnostics.fileCount > 0) {
      message += ` (${diagnostics.fileCount} files found)`;
    }
    if (diagnostics.publicUrlTest === true) {
      message += " - Public URLs working";
    } else if (diagnostics.publicUrlTest === false) {
      message += " - Public URL access may be limited";
    }
    
    return {
      success: true,
      message,
      diagnostics,
      directAccessWorking,
      recommendations: generateStorageRecommendations(diagnostics, directAccessWorking)
    };
    
  } catch (error) {
    logger.error("❌ Storage diagnostics failed:", error);
    return {
      success: false,
      error,
      message: `Storage diagnostics failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      recommendations: [
        "Check network connectivity",
        "Verify Supabase project configuration",
        "Ensure storage bucket exists and is properly configured"
      ]
    };
  }
};

/**
 * Generate actionable recommendations based on storage diagnostics
 */
const generateStorageRecommendations = (diagnostics: any, directAccessWorking: boolean): string[] => {
  const recommendations: string[] = [];
  
  if (!diagnostics.bucketExists) {
    recommendations.push("Create the 'product-images' storage bucket");
    recommendations.push("Ensure bucket is set to public if images should be publicly accessible");
  }
  
  if (!diagnostics.canList && !directAccessWorking) {
    recommendations.push("Check Row Level Security (RLS) policies on storage.objects");
    recommendations.push("Verify user authentication state");
  }
  
  if (diagnostics.canList && diagnostics.fileCount === 0) {
    recommendations.push("Upload some test images to verify the upload process");
  }
  
  if (diagnostics.publicUrlTest === false) {
    recommendations.push("Check CORS settings for the storage bucket");
    recommendations.push("Verify bucket public access settings");
  }
  
  if (!diagnostics.canRead && !directAccessWorking) {
    recommendations.push("Test image URLs in a new browser tab to isolate CORS issues");
    recommendations.push("Check if images are accessible when logged in as admin");
  }
  
  if (recommendations.length === 0) {
    recommendations.push("Storage appears to be working correctly");
    recommendations.push("If you're still experiencing issues, try clearing the image cache");
  }
  
  return recommendations;
};
